<script setup lang="ts">
import { Bell, CheckCheck, Loader2 } from '@lucide/vue';

import { useNotificationsI18n } from '../composables/useNotificationsI18n';
import type { NotificationData } from '../types/notification';

import NotificationItem from './NotificationItem.vue';

const { t } = useNotificationsI18n();

defineProps<{
    notifications: NotificationData[];
    isLoading: boolean;
}>();

const emit = defineEmits<{
    markRead: [id: string];
    markAllRead: [];
    delete: [id: string];
}>();

function handleMarkRead(id: string) {
    emit('markRead', id);
}

function handleMarkAllRead() {
    emit('markAllRead');
}

function handleDelete(id: string) {
    emit('delete', id);
}
</script>

<template>
    <div class="flex flex-col">
        <!-- Header -->
        <div class="flex items-center justify-between border-b px-3 py-2">
            <h3 class="text-sm font-semibold">{{ t('notifications.title') }}</h3>
            <button
                v-if="notifications.length > 0"
                class="text-muted-foreground hover:text-foreground flex items-center gap-1 text-xs transition-colors"
                @click="handleMarkAllRead"
            >
                <CheckCheck class="size-3" />
                {{ t('notifications.mark_all_read') }}
            </button>
        </div>

        <!-- Loading state -->
        <div v-if="isLoading" class="flex items-center justify-center py-8">
            <Loader2 class="text-muted-foreground size-6 animate-spin" />
        </div>

        <!-- Empty state -->
        <div v-else-if="notifications.length === 0" class="flex flex-col items-center justify-center py-8">
            <Bell class="text-muted-foreground/50 size-10" />
            <p class="text-muted-foreground mt-2 text-sm">{{ t('notifications.no_notifications') }}</p>
        </div>

        <!-- Notification list -->
        <div v-else class="max-h-80 overflow-y-auto">
            <NotificationItem
                v-for="notification in notifications"
                :key="notification.id"
                :notification="notification"
                @mark-read="handleMarkRead"
                @delete="handleDelete"
            />
        </div>
    </div>
</template>
